import React, {memo, useEffect, useState} from 'react'
import {Form as FormikForm, Formik} from 'formik'
import api from '../../../api'
import ComponentButton from '../../components/ComponentButton'
import ComponentTextarea from '../../components/ComponentTextarea'
import ComponentRange from '../../components/ComponentRange'
import {TModuleTakeTest, TTestItem} from './_types'
import useTest from './helpers/useTest'
import {useIntl} from 'react-intl'
import TestRadio from './src/TestRadio'
import TestCheckbox from './src/TestCheckBox'
import {getErrorToast} from '../../helpers/toasts'
import {useAuth} from '../../../modules/auth'
import TestProgressBar from './src/TestProgressBar'

const ModuleTakeTest: React.FC<TModuleTakeTest> = memo(({settings}) => {
    const auth = useAuth()
    const intl = useIntl()
    const testId = settings.filters.id
    const {
        data: test,
        isLoading,
        refetch,
    } = useTest<TTestItem>(
        settings.object,
        {
            id: testId,
            context: settings?.context?.page,
        },
        true
    )
    const [isFetch, setIsFetch] = useState(false)
    const [countPoint, setCountPoint] = useState(0)
    const [step, setStep] = useState<number>(() =>
        Number(localStorage.getItem(`test-${testId}-step`) ?? 0)
    )
    const [answers, setAnswers] = useState<Record<number, any>>(() => {
        try {
            return JSON.parse(localStorage.getItem(`test-${testId}-answers`) ?? '{}')
        } catch {
            return {}
        }
    })

    const [isFinished, setIsFinished] = useState(false)
    const [result, setResult] = useState<string | null>(null)

    useEffect(() => {
        localStorage.setItem(`test-${testId}-step`, String(step))
    }, [step, testId])

    useEffect(() => {
        if (test?.isCompleted) {
            localStorage.removeItem(`test-${testId}-step`)
            localStorage.removeItem(`test-${testId}-answers`)
            localStorage.removeItem(`test-${testId}-points`)
        }
    }, [testId, test])

    const validateCurrentAnswer = (value: any, type: string): boolean => {
        switch (type) {
            case 'text':
                return typeof value === 'string' && value.trim().length > 0
            case 'range':
                return typeof value === 'number' || /^\d+$/.test(value)
            case 'single':
                return typeof value === 'number'
            case 'multiple':
                return Array.isArray(value) && value.length > 0
            default:
                return false
        }
    }

    const handleAnswerSubmit = (questionId: number, value: any, isLastStep: boolean) => {
        const updatedAnswers = {
            ...answers,
            [questionId]: value,
        }

        localStorage.setItem(`test-${testId}-answers`, JSON.stringify(updatedAnswers))

        setAnswers(updatedAnswers)

        if (isLastStep) {
            finishTest(updatedAnswers)
        } else {
            setStep(step + 1)
        }
    }

    const handleBack = (value: any, questionId: number) => {
        const updatedAnswers = {
            ...answers,
            [questionId]: value,
        }

        localStorage.setItem(`test-${testId}-answers`, JSON.stringify(updatedAnswers))
        setAnswers(updatedAnswers)

        if (step > 0) {
            setStep(step - 1)
        }
    }

    const finishTest = async (finalAnswers: Record<number, any>) => {
        if (!test) return
        setIsFetch(true)

        const resultString = ''

        const preparedQuestions = test.questions.map((q: any, i: number) => {
            const answer = finalAnswers[q.id]
            const prepared: any = {
                text: q.text,
                type: q.type,
                position: Number.isFinite(q.position) ? Number(q.position) : i, // ← ФИКС
                answers: [],
            }

            if (q.type === 'text' || q.type === 'range') {
                prepared.answers = [{text: answer?.toString() ?? '', isUserAnswer: true}]
            } else if (q.type === 'single') {
                prepared.answers = q.answers.map((a: any) => ({
                    text: a.text,
                    isUserAnswer: Number(a.id) === Number(answer),
                }))
            } else if (q.type === 'multiple') {
                prepared.answers = q.answers.map((a: any) => ({
                    text: a.text,
                    isUserAnswer: Array.isArray(answer) && answer.includes(a.id),
                }))
            }

            return prepared
        })

        const payload = {
            test_id: test.id,
            client_id: auth.currentUser?.id,
            user_id: test.users_id?.[0]?.value ?? null,
            questions: preparedQuestions,
        }

        const response = await api('testsResults', 'add', payload)

        localStorage.removeItem(`test-${testId}-step`)
        localStorage.removeItem(`test-${testId}-answers`)

        setCountPoint(response.data.countPoints)
        setResult(response.data.defaultResult)
        setIsFetch(false)
        setIsFinished(true)
    }

    const renderQuestion = (
        question: any,
        formik: {values: any; setFieldValue: (field: string, value: any) => void}
    ) => {
        const {type, answers: options, from, to} = question
        const {values, setFieldValue} = formik

        switch (type) {
            case 'text':
                return (
                    <>
                        <span className='moduleTakeTest_question_title'>{question.text}</span>
                        <ComponentTextarea article='answer' />
                    </>
                )
            case 'range':
                return (
                    <>
                        <span className='moduleTakeTest_question_title'>{question.text}</span>
                        <div className='moduleTakeTest_question_answer'>
                            <ComponentRange article='answer' min={from ?? 0} max={to ?? 10} />
                        </div>
                    </>
                )
            case 'single':
                return (
                    <>
                        <span className='moduleTakeTest_question_title'>{question.text}</span>
                        <TestRadio
                            article='answer'
                            value={values.answer}
                            onChange={(val) => setFieldValue('answer', val)}
                            options={question.answers.map((a: any) => ({
                                label: a.text,
                                value: a.id,
                            }))}
                        />
                    </>
                )
            case 'multiple':
                return (
                    <>
                        <span className='moduleTakeTest_question_title'>{question.text}</span>
                        <TestCheckbox
                            article='answer'
                            value={values.answer ?? []}
                            onChange={(val) => setFieldValue('answer', val)}
                            options={question.answers.map((a: any) => ({
                                label: a.text,
                                value: a.id,
                            }))}
                        />
                    </>
                )
            default:
                return null
        }
    }

    if (isLoading || !test) {
        return (
            <div className='moduleTakeTest card mt-8 d-flex align-items-center justify-content-center h-100'>
                <div className='card-body'>
                    <svg className='splash-spinner' viewBox='0 0 50 50'>
                        <circle
                            className='path'
                            cx='25'
                            cy='25'
                            r='20'
                            fill='none'
                            strokeWidth='5'
                        ></circle>
                    </svg>
                </div>
            </div>
        )
    }

    const question = test.questions[step]

    const isLastStep = step === test.questions.length - 1

    return (
        <div className='moduleTakeTest card mt-8'>
            <div className='moduleTakeTest_card-header card-header'>
                <div className='moduleTakeTest_card-title card-title'>{test.title}</div>
            </div>
            {!test.isCompleted && <TestProgressBar current={step} total={test.questions.length} />}
            <div className='moduleTakeTest_card-body card-body'>
                {test.userResult ? (
                    <div className='alert alert-primary fs-5'>{test.userResult}</div>
                ) : isFinished ? (
                    result ? (
                        <div className='alert alert-primary fs-5'>
                            {Number.isFinite(countPoint) && Number(countPoint) > 0 && (
                                <>
                                    Ваши баллы: {countPoint} <br />
                                </>
                            )}
                            {result}
                        </div>
                    ) : (
                        <div className='alert alert-primary fs-5'>Благодарим за участие!</div>
                    )
                ) : test.isCompleted ? (
                    <div className='alert alert-warning fs-5' role='alert'>
                        Тест уже пройден. Повторное прохождение невозможно.
                        <br />
                        Дождитесь проверки экспертом.
                    </div>
                ) : (
                    <Formik
                        key={question.id}
                        initialValues={{
                            answer: answers[question.id] ?? (question.type === 'multiple' ? [] : question.type === 'single' ? null : '')
                        }}
                        onSubmit={(values) => {
                            if (!validateCurrentAnswer(values.answer, question.type)) {
                                getErrorToast('Пожалуйста, ответьте на вопрос перед продолжением')
                                return
                            }

                            handleAnswerSubmit(question.id, values.answer, isLastStep)
                        }}
                    >
                        {({handleSubmit, values, setFieldValue}) => (
                            <FormikForm>
                                <div className='moduleTakeTest_question_wrapper mb-6 mt-2'>
                                    {renderQuestion(question, {values, setFieldValue})}
                                </div>
                                <div className='mt-6 d-flex'>
                                    {step > 0 && (
                                        <ComponentButton
                                            type='custom'
                                            settings={{
                                                title: 'Назад',
                                                background: 'light',
                                                icon: '',
                                            }}
                                            customHandler={() => handleBack(values.answer, question.id)}
                                        />
                                    )}
                                    <div style={{ marginLeft: 'auto' }}>
                                        <ComponentButton
                                            type='custom'
                                            settings={{
                                                title: isLastStep
                                                    ? intl.formatMessage({id: 'BUTTON.END'})
                                                    : intl.formatMessage({id: 'BUTTON.NEXT'}),
                                                background: 'dark',
                                                icon: '',
                                            }}
                                            loading={isFetch}
                                            customHandler={handleSubmit}
                                        />
                                    </div>
                                </div>
                            </FormikForm>
                        )}
                    </Formik>
                )}
            </div>
        </div>
    )
})

export default ModuleTakeTest
